import Node from './node'
import Link from './link'

import canvasActions from './canvasActions'
import canvasLogic from './canvasLogic'

import thoughtCatcherModule from './thoughtCatcher/thoughtCatcher'
import HMWModule from './HMW/HMW'
import factsModule from './facts/facts'
import Vue from 'vue'

import { downloadPDF } from './pdfBuilder'
import { v4 as uuidv4 } from 'uuid'

function centerCanvas (state) {
  if ((state.nodes.length > 0) && state.diagramCtx) {
    const canvas = state.diagramCtx.canvas
    const bounds = canvasLogic.getMaxBounds(state)

    bounds.maxX += 40
    bounds.maxY += 40
    bounds.minX -= 40
    bounds.minY -= 40

    const xsize = bounds.maxX - bounds.minX
    const ysize = bounds.maxY - bounds.minY
    const xzoom = canvas.width / xsize
    const yzoom = canvas.height / ysize

    let zoom = 1
    if (xzoom > yzoom) {
      zoom = yzoom
    } else {
      zoom = xzoom
    }

    if (zoom > canvasLogic.maxZoom) zoom = canvasLogic.maxZoom
    if (zoom < canvasLogic.minZoom) zoom = canvasLogic.minZoom
    state.canvasZoom = zoom

    const left = bounds.minX - (canvas.width / zoom - xsize) / 2
    const top = bounds.minY - (canvas.height / zoom - ysize) / 2
    state.canvasPos = [left * zoom, top * zoom]
  } else {
    console.log('centerCanvas: NO NODES')
  }
}

function processMessage (event) {
  const msg = JSON.parse(event.data)
  const state = MODULE.state

  // splice action array so it only contains actions that the backend hasnt processed yet
  const removedActions = state.actions.splice(0, msg.actionCount - state.lastStateActionIndex)
  state.lastStateActionIndex = msg.actionCount

  // copy recieved state into new endState
  state.endBaseState = {
    nodes: [],
    nodeMap: {},
    links: [],
    linkMap: {}
  }

  msg.nodes.sort((a, b) => { return a.id - b.id })

  msg.nodes.forEach(node => {
    const newNode = new Node(
      node.id, [node.x, node.y], [node.width, node.height],
      node.fill, node.text, node.font, node.fontColor, node.dotColor, node.pinCount, node.pinnedByMe, node.pinned
    )

    state.endBaseState.nodes.push(newNode)
    state.endBaseState.nodeMap[newNode.id] = newNode
  })

  msg.links.forEach(link => {
    const newLink = new Link(link.id,
      state.endBaseState.nodeMap[link.Aid], state.endBaseState.nodeMap[link.Bid],
      link.thickness, link.color
    )

    state.endBaseState.links.push(newLink)
    state.endBaseState.linkMap[newLink.id] = newLink
  })

  // copy the current local state and apply removedActions
  const advancedLocalState = {
    nodes: state.nodes,
    nodeMap: state.nodeMap,
    links: state.links,
    linkMap: state.linkMap
  }

  for (let i = 0; i < removedActions.length; i++) {
    canvasLogic.applyAction(advancedLocalState, removedActions[i])
  }

  // compute startBaseState
  state.startBaseState = {
    nodes: [],
    nodeMap: {},
    links: [],
    linkMap: {}
  }

  state.endBaseState.nodes.forEach(node => {
    let pos = [node.pos[0], node.pos[1]]
    let size = [node.size[0], node.size[1]]

    if (advancedLocalState.nodeMap[node.id] !== undefined) {
      const localNode = advancedLocalState.nodeMap[node.id]
      pos = [localNode.pos[0], localNode.pos[1]]
      size = [localNode.size[0], localNode.size[1]]
    }

    const newNode = new Node(
      node.id, pos, size,
      node.fill, node.text, node.font, node.fontColor, node.dotColor, node.pins, node.pinnedByMe, node.pinned
    )

    state.startBaseState.nodes.push(newNode)
    state.startBaseState.nodeMap[newNode.id] = newNode
  })

  state.endBaseState.links.forEach(link => {
    const newLink = new Link(link.id,
      state.startBaseState.nodeMap[link.A.id], state.startBaseState.nodeMap[link.B.id],
      link.thickness, link.color
    )

    state.startBaseState.links.push(newLink)
    state.startBaseState.linkMap[newLink.id] = newLink
  })

  state.lastBaseStateChange = Date.now() / 1000

  if (!state.gotInitialState) {
    canvasLogic.draw(state)
    centerCanvas(state)
    canvasLogic.draw(state)
    state.gotInitialState = true
  }

  const blendLoop = function () {
    const epochSec = Date.now() / 1000
    canvasLogic.draw(MODULE.state)
    if (epochSec - state.lastBaseStateChange <= state.baseStateBlendTime) {
      canvasLogic.draw(MODULE.state)
      window.requestAnimationFrame(blendLoop)
    }
  }
  window.requestAnimationFrame(blendLoop)
}

function doAction (action) {
  MODULE.state.socket.send(JSON.stringify(action))
  MODULE.state.actions.push(action)
  canvasLogic.draw(MODULE.state)
}

function socketReady () {
  return (MODULE.state.socket !== null) && (MODULE.state.socket.readyState === 1)
}

const MODULE = {
  namespaced: true,
  state: {
    // canvas connection state
    socket: null,
    lastStateActionIndex: 0, // Number of actions from this connection that the server has processed
    currentlySettingUpSocket: false,
    participants: [],

    // Canvas UI state
    diagramCtx: null, // The current canvas 2d context
    minimapCtx: null, // The current canvas 2d context
    canvasPos: [0, 0], // The offset the canvas is at
    canvasZoom: 1, // The zoom scale the canvas is at
    dndOverDiagram: false, // If the object being drag and dropped is over the diagram

    mousePos: [0, 0],
    touch0: null,
    touch1: null,
    mouseStart: null,
    touchStart: null,
    selectedNodeID: null, // node that was clicked, null if none
    selectedNodePinsID: null, // node ID of pin that was clicked, used for showing pins modal
    selectedLinkID: null, // link that was clicked, null if none
    canvasAction: canvasActions.Default,
    canvasActionData: {},

    // internal canvas state tracking, used for client side state prediction, blending, etc...
    actions: [],

    startBaseState: {
      nodes: [],
      nodeMap: {},
      links: [],
      linkMap: {}
    },

    endBaseState: {
      nodes: [],
      nodeMap: {},
      links: [],
      linkMap: {}
    },

    gotInitialState: false,
    lastBaseStateChange: 0,
    baseStateBlendTime: 1 / 5,

    // Current local canvas state
    nodes: [],
    nodeMap: {},
    links: [],
    linkMap: {}
  },
  mutations: {
    // Canvas UI related mutations
    setCanvas (state, { diagramCtx, minimapCtx }) {
      state.diagramCtx = diagramCtx
      state.minimapCtx = minimapCtx
    },
    removeCanvasSocket (state) {
      state.socket = null
    },
    setDnDOverDiagram (state, dndOverDiagram) {
      console.log(`setDnDOverDiagram ${dndOverDiagram}`)
      state.dndOverDiagram = dndOverDiagram
    },
    setSelectedNodeID (state, nodeID) {
      state.selectedNodeID = nodeID
      state.selectedLinkID = null
      state.selectedNodePinsID = null
    },
    setSelectedNodePinsID (state, nodeID) {
      state.selectedNodePinsID = nodeID
      state.selectedNodeID = null
      state.selectedLinkID = null
    },
    setSelectedLinkID (state, linkID) {
      state.selectedLinkID = linkID
      state.selectedNodeID = null
      state.selectedNodePinsID = null
    },
    setCanvasAction (state, canvasAction) {
      state.canvasAction = canvasAction
      state.canvasActionData = {}
      canvasLogic.draw(state)
    },
    setParticipants (state, participants) {
      Vue.set(state, 'participants', participants)
    }
  },
  actions: {
    setupCanvasSocket: async ({ state, commit, dispatch, rootState }, token) => {
      if (!state.currentlySettingUpSocket) {
        state.currentlySettingUpSocket = true

        // setup auth query param
        const url = process.env.VUE_APP_WS_BASE + '/rt/canvas?' + (new URLSearchParams({
          auth: token
        }).toString())

        // close old socket
        if (state.socket !== null) {
          state.socket.close()
        }

        // wipe all the client side prediction stuff
        state.actions = []

        state.startBaseState = {
          nodes: [],
          nodeMap: {},
          links: [],
          linkMap: {}
        }

        state.endBaseState = {
          nodes: [],
          nodeMap: {},
          links: [],
          linkMap: {}
        }

        state.gotInitialState = false
        state.lastBaseStateChange = 0

        // create new socket
        state.socket = new WebSocket(url)
        state.socket.onmessage = processMessage
        state.lastStateActionIndex = 0
        console.log('CANVAS SOCKET CREATED')

        state.currentlySettingUpSocket = false

        return state.socket.onerror
      }
    },

    async eventSetParticipants (context, participants) {
      const audioParticipants = []
      for (const p of participants) {
        const user = await context.dispatch('requestSearchUserByUUID', p, { root: true })
        const avatar = await context.dispatch('getAvatarFromUserName', user.name, { root: true })
        user.avatar = avatar

        if (user.uuid === context.rootState.auth.userInfo.account.uuid) {
          user.name = user.name + ' (Me)'
        } else if (user.uuid === '') {
          user.uuid = uuidv4()
          user.name = user.email
        }

        audioParticipants.push(user)
      }

      context.commit('setParticipants', audioParticipants)
      context.commit('setTabBadgeNumber', {
        drawerName: 'whiteboard',
        tabName: 'participantsTab',
        badgeNumber: audioParticipants.length
      }, { root: true })
    },

    // Actions that modify the canvas (these correspond to actions that the backend understands)
    moveNode ({ state, commit }, { nodeID, newx, newy }) {
      if (!socketReady()) return

      doAction({
        actionType: 'moveNode',
        action: {
          nodeID: nodeID,
          newx: newx,
          newy: newy
        }
      })
    },
    resizeNode ({ state, commit }, { nodeID, newWidth, newHeight }) {
      if (!socketReady()) return

      doAction({
        actionType: 'resizeNode',
        action: {
          nodeID: nodeID,
          newWidth: newWidth,
          newHeight: newHeight
        }
      })
    },
    changeNodeText ({ state, commit }, { nodeID, newText }) {
      if (!socketReady()) return

      const node = state.nodeMap[nodeID]
      const fitSize = node.getFitSize(state.diagramCtx, newText, node.font, node.size[0], node.size[1])
      doAction({
        actionType: 'resizeNode',
        action: {
          nodeID: nodeID,
          newWidth: fitSize.width,
          newHeight: fitSize.height
        }
      })

      doAction({
        actionType: 'changeNodeText',
        action: {
          nodeID: nodeID,
          newText: newText
        }
      })
    },
    changeNodeFill ({ state, commit }, { nodeID, newFill }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeNodeFill',
        action: {
          nodeID: nodeID,
          newFill: newFill
        }
      })
    },
    changeNodeFont ({ state, commit }, { nodeID, newFont }) {
      if (!socketReady()) return

      const node = state.nodeMap[nodeID]
      const fitSize = node.getFitSize(state.diagramCtx, node.text, newFont, node.size[0], node.size[1])
      doAction({
        actionType: 'resizeNode',
        action: {
          nodeID: nodeID,
          newWidth: fitSize.width,
          newHeight: fitSize.height
        }
      })

      doAction({
        actionType: 'changeNodeFont',
        action: {
          nodeID: nodeID,
          newFont: newFont
        }
      })
    },
    changeNodeFontColor ({ state, commit }, { nodeID, newFontColor }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeNodeFontColor',
        action: {
          nodeID: nodeID,
          newFontColor: newFontColor
        }
      })
    },
    changeNodeDotColor ({ state, commit }, { nodeID, newDotColor }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeNodeDotColor',
        action: {
          nodeID: nodeID,
          newDotColor: newDotColor
        }
      })
    },
    changeNodePin ({ state, commit }, { nodeID, pinState }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeNodePin',
        action: {
          nodeID: nodeID,
          pinState: pinState
        }
      })
    },
    deleteNode ({ state, commit }, { nodeID }) {
      if (!socketReady()) return

      doAction({
        actionType: 'deleteNode',
        action: {
          nodeID: nodeID
        }
      })
    },
    createNode ({ state, commit }, { x, y, width, height, text, fill, font, fontColor, dotColor }) {
      if (!socketReady()) return

      doAction({
        actionType: 'createNode',
        action: {
          x: x,
          y: y,
          width: width,
          height: height,
          text: text,
          fill: fill,
          font: font,
          fontColor: fontColor,
          dotColor: dotColor
        }
      })
    },

    createNodeFromThought ({ state, commit, rootState }, { x, y, width, height, fill, font, fontColor, dotColor, thoughtID }) {
      if (!socketReady()) return

      let thoughtIndex = null
      for (let i = 0; i < state.thoughtCatcher.publicThoughts.length; i++) {
        if (state.thoughtCatcher.publicThoughts[i].uuid === thoughtID) {
          thoughtIndex = i
          break
        }
      }

      if (thoughtIndex !== null) {
        const thought = state.thoughtCatcher.publicThoughts[thoughtIndex]

        const fitNode = new Node() /* TODO(Jon): this is really sloppy */
        const fitSize = fitNode.getFitSize(state.diagramCtx, thought.content, font, width, height)

        doAction({
          actionType: 'createNodeFromThought',
          action: {
            x: x,
            y: y,
            width: fitSize.width,
            height: fitSize.height,
            fill: fill,
            font: font,
            fontColor: fontColor,
            dotColor: dotColor,
            thoughtID: thoughtID
          }
        })
      }
    },

    createNodeFromHMW ({ state, commit, rootState }, { x, y, width, height, fill, font, fontColor, dotColor, HMWID }) {
      if (!socketReady()) return

      let HMWIndex = null
      for (let i = 0; i < state.HMW.HMWs.length; i++) {
        if (state.HMW.HMWs[i].uuid === HMWID) {
          HMWIndex = i
          break
        }
      }

      if (HMWIndex !== null) {
        const HMW = state.HMW.HMWs[HMWIndex]

        const fitNode = new Node() /* TODO(Jon): this is really sloppy */
        const fitSize = fitNode.getFitSize(state.diagramCtx, HMW.content, font, width, height)

        doAction({
          actionType: 'createNodeFromHMW',
          action: {
            x: x,
            y: y,
            width: fitSize.width,
            height: fitSize.height,
            fill: fill,
            font: font,
            fontColor: fontColor,
            dotColor: dotColor,
            HMWID: HMWID
          }
        })
      }
    },

    createNodeFromFact ({ state, commit, rootState }, { x, y, width, height, fill, font, fontColor, dotColor, factID }) {
      if (!socketReady()) return

      let factIndex = null
      for (let i = 0; i < state.facts.facts.length; i++) {
        if (state.facts.facts[i].uuid === factID) {
          factIndex = i
          break
        }
      }

      if (factIndex !== null) {
        const fact = state.facts.facts[factIndex]

        const fitNode = new Node() /* TODO(Jon): this is really sloppy */
        const fitSize = fitNode.getFitSize(state.diagramCtx, fact.content, font, width, height)

        doAction({
          actionType: 'createNodeFromFact',
          action: {
            x: x,
            y: y,
            width: fitSize.width,
            height: fitSize.height,
            fill: fill,
            font: font,
            fontColor: fontColor,
            dotColor: dotColor,
            factID: factID
          }
        })
      }
    },

    createLink ({ state, commit }, { nodeAID, nodeBID, thickness, color }) {
      if (!socketReady()) return

      doAction({
        actionType: 'createLink',
        action: {
          nodeAID: nodeAID,
          nodeBID: nodeBID,
          thickness: thickness,
          color: color
        }
      })
    },
    changeLinkThickness ({ state, commit }, { linkID, newThickness }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeLinkThickness',
        action: {
          linkID: linkID,
          newThickness: newThickness
        }
      })
    },
    changeLinkColor ({ state, commit }, { linkID, newColor }) {
      if (!socketReady()) return

      doAction({
        actionType: 'changeLinkColor',
        action: {
          linkID: linkID,
          newColor: newColor
        }
      })
    },
    deleteLink ({ state, commit }, { linkID }) {
      if (!socketReady()) return

      doAction({
        actionType: 'deleteLink',
        action: {
          linkID: linkID
        }
      })
    },

    // Canvas UI Mouse interactions
    onMouseMove (context, { x, y }) {
      canvasLogic.onMouseMove(context, { x: x, y: y })
      canvasLogic.draw(context.state)
    },
    onMouseDown (context) {
      canvasLogic.onMouseDown(context)
      canvasLogic.draw(context.state)
    },
    onMouseUp (context) {
      canvasLogic.onMouseUp(context)
      canvasLogic.draw(context.state)
    },
    onMouseWheel (context, delta) {
      canvasLogic.onMouseWheel(context, delta)
      canvasLogic.draw(context.state)
    },

    // Canvas UI Touch Interactions
    onTouchMove (context, touches) {
      canvasLogic.onTouchMove(context, touches)
      canvasLogic.draw(context.state)
    },
    onTouchStart (context, touches) {
      canvasLogic.onTouchStart(context, touches)
      canvasLogic.draw(context.state)
    },
    onTouchEnd (context) {
      canvasLogic.onTouchEnd(context)
      canvasLogic.draw(context.state)
    },

    // Other Canvas UI related actions
    centerCanvas (context) {
      centerCanvas(context.state)
      canvasLogic.draw(context.state)
    },

    drawCanvas (context) {
      canvasLogic.draw(context.state)
    },

    downloadCanvas (context) {
      // calculate bounds
      const bounds = canvasLogic.getMaxBounds(context.state)
      bounds.maxX += 40
      bounds.maxY += 40
      bounds.minX -= 40
      bounds.minY -= 40

      const minXSize = 400
      const minYSize = 200

      if ((bounds.maxX - bounds.minX) < minXSize) {
        const xdiff = minXSize - (bounds.maxX - bounds.minX)
        bounds.maxX += xdiff / 2
        bounds.minX -= xdiff / 2
      }

      if ((bounds.maxY - bounds.minY) < minYSize) {
        const ydiff = minYSize - (bounds.maxY - bounds.minY)
        bounds.maxY += ydiff / 2
        bounds.minY -= ydiff / 2
      }

      const zoom = 1
      const xsize = bounds.maxX - bounds.minX
      const ysize = bounds.maxY - bounds.minY
      const left = bounds.minX
      const top = bounds.minY

      // create offscreen canvas
      const offScreenCanvas = document.createElement('canvas')
      offScreenCanvas.width = xsize * zoom
      offScreenCanvas.height = ysize * zoom
      const ctx = offScreenCanvas.getContext('2d', { alpha: false })

      // draw canvas
      canvasLogic.drawDiagram(context.state, ctx, [left * zoom, top * zoom], zoom)

      downloadPDF(context, offScreenCanvas.toDataURL('image/jpeg'), { w: offScreenCanvas.width, h: offScreenCanvas.height })
    }
  },

  modules: {
    thoughtCatcher: thoughtCatcherModule,
    HMW: HMWModule,
    facts: factsModule
  }
}

export default MODULE
